import {
  ProductNav,
  ProductNavLogo,
  ProductNavLink,
  ProductNavList,
  ProductNavDropdown,
  ProductNavSublink,
} from 'eds-product-experience-ux';
import { useMatches } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import {
  useGetFilteredContentApi,
  type FilteredContent,
  type SubSection,
  type LinkItem,
} from 'eds-common/api/v1';
import { useGetMeApi } from 'eds-common/api/v2';
import { useGetClassesApi } from 'eds-common/api/v3';

import type { URIMatch } from '@/types/uri';
import {
  CLASSES_TAB_PRODUCTS,
  PRODUCT_SECTION_ROUTES,
  PRODUCT_SUBSECTION_ROUTES,
  ROUTE_PRODUCT_MAPPING,
} from '@/constants';

import LaunchInNewTabIcon from '@/assets/icons/launch-in-new-tab-icon.svg';
import { useApplications } from '@/hooks/api/use-application/use-application';

function getSectionUrl(product: string, path: string, id: string) {
  const sectionRoute = PRODUCT_SECTION_ROUTES[product] ?? '';
  return `${path}${sectionRoute}/${id}`;
}

function getSubSectionUrl(product: string, path: string, id: string) {
  const subsectionRoute = PRODUCT_SUBSECTION_ROUTES[product] ?? '';
  return `${path}${subsectionRoute}/${id}`;
}

function getSubNavItemsFromSubSections(
  data: SubSection[],
  product: string,
  path: string
) {
  return data.map((subsection) => {
    const route = subsection.parentId
      ? getSubSectionUrl(
          product,
          getSectionUrl(product, path, subsection.parentId),
          subsection.id
        )
      : getSectionUrl(product, path, subsection.id);
    return (
      <ProductNavSublink key={subsection.id} to={route}>
        {subsection.metadata.title}
      </ProductNavSublink>
    );
  });
}

function getSubNavItemsFromLinks(data: LinkItem[]) {
  return data.map((link) => {
    return (
      <ProductNavSublink
        to={link.href}
        key={link.title}
        className="flex items-center gap-2"
        target="_blank"
      >
        {link.title}
        <LaunchInNewTabIcon aria-label="Opens in a new tab" />
      </ProductNavSublink>
    );
  });
}

function getNavItems(data: FilteredContent[], product: string, path: string) {
  return data.map((navContent) => {
    // handle dropdown for subsections
    if (navContent.subSections && navContent.subSections.length > 0) {
      return (
        <ProductNavDropdown key={navContent.id} label={navContent.title}>
          {getSubNavItemsFromSubSections(navContent.subSections, product, path)}
          {navContent.type === 'section' && (
            <ProductNavSublink
              to={getSectionUrl(product, path, navContent.id ?? '')}
              className="font-bold"
            >
              {`View All ${navContent.title}`}
            </ProductNavSublink>
          )}
        </ProductNavDropdown>
      );
    }
    // handle dropdown for links
    if (navContent.links && navContent.links.length > 0) {
      return (
        <ProductNavDropdown key={navContent.id} label={navContent.title}>
          {getSubNavItemsFromLinks(navContent.links)}
        </ProductNavDropdown>
      );
    }
    // handle section link in nav
    return (
      <ProductNavLink
        key={navContent.id}
        to={getSectionUrl(product, path, navContent.id ?? '')}
      >
        {navContent.title}
      </ProductNavLink>
    );
  });
}

function getPath(matches: URIMatch[]) {
  const match = matches.find((m) => m.pathname in ROUTE_PRODUCT_MAPPING);
  return match ? match.pathname : '/';
}

export function Navigation() {
  const matches = useMatches();
  const path = getPath(matches);
  const product = ROUTE_PRODUCT_MAPPING[path];
  const { data: applications } = useApplications();
  const { data: navData, isError } = useGetFilteredContentApi({
    product: product ?? '',
    filterId: 'nav',
  });
  const { data: user } = useGetMeApi();
  const { data: classes } = useGetClassesApi({
    userId: user?.userId ?? '',
    orgId: user?.currentOrg?.orgId ?? '',
    source: user?.userSource ?? 'MyScholastic',
  });
  const [hasClasses, setHasClasses] = useState<boolean>(false);

  const navSections = useMemo(
    () => navData && product && getNavItems(navData, product, path),
    [navData, path, product]
  );

  const application = useMemo(
    () =>
      applications?.filter(
        ({ applicationCode }) => applicationCode === product
      )[0],
    [applications, product]
  );

  useEffect(() => {
    if (
      classes &&
      classes.length > 0 &&
      CLASSES_TAB_PRODUCTS.includes(product ?? '')
    ) {
      setHasClasses(true);
    }
  }, [classes, product]);

  const productLogo = application ? application?.iconSrc : undefined;
  const productLogoAltText = `${application?.applicationTitle} Homepage`;

  return product ? (
    <ProductNav className="border-b border-b-neutral-400">
      <ProductNavLogo src={productLogo} alt={productLogoAltText} href={path} />
      <ProductNavList>
        {!isError && navSections}
        {hasClasses && (
          <ProductNavLink to="/data">Class Insights</ProductNavLink>
        )}
      </ProductNavList>
    </ProductNav>
  ) : null;
}
